/**
 * `<PrepHistoryAccordion>` — collapsed accordion of prior prep instances
 * shown below the active-stage section on the Interview Prep tab.
 *
 * Phase 2.6 — interview-prep-spec.md §5.
 *
 * Each entry is a collapsible row. Closed state shows the eyebrow stage
 * label + stage name + generated date + StatusPill (§5.2) + chevron.
 * Open state shows the full prep content via `<EvaluationMarkdown>` (reuse
 * — no fork per Phase 2.5 HoD §4.7 ratified rule).
 *
 * Phase 2.6.2 (S1):
 *   S1 — closed row: StatusPill in right cluster + optional truncated
 *        provenance line (absent when history endpoint returns no
 *        per-prep provenance — graceful degrade per orphan comment at
 *        InterviewPrepTab.tsx:271).
 *
 * S2 (Regenerate affordance) REMOVED (auto-eval-prep): prep artifacts are
 * deterministic. The "Regenerate this prep" button is no longer surfaced.
 *
 * Cofounder Decision 2 PERMANENT CANON: NO per-question recruiter notes
 * thread. This component does NOT render any composer / notes thread
 * for past preps — the recruiter's input surface is exclusively the
 * Analysis tab's paste-box.
 */
import { useState, type JSX } from "react";
import { ChevronDown } from "lucide-react";
import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import type { InterviewPrepHistoryRow } from "@/hooks/queries/interview-prep/useInterviewPrepHistory";

interface PrepHistoryAccordionProps {
  rows: InterviewPrepHistoryRow[];
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function PrepHistoryAccordion({
  rows,
}: PrepHistoryAccordionProps): JSX.Element | null {
  if (rows.length === 0) return null;

  return (
    <section
      className="mt-12 border-t border-border-default pt-8"
      data-testid="prep-history"
    >
      <header className="mb-4 flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-text-emphasis">
          Prep history
        </h3>
        <p className="text-sm text-text-secondary">
          {rows.length} prior interview prep{" "}
          {rows.length === 1 ? "guide" : "guides"}
        </p>
      </header>
      <ul className="flex flex-col gap-3">
        {rows.map((row) => (
          <PrepHistoryEntry
            key={row.id}
            row={row}
          />
        ))}
      </ul>
    </section>
  );
}

interface PrepHistoryEntryProps {
  row: InterviewPrepHistoryRow;
}

/**
 * S1 (§5.2): Compute "filled" vs "skipped" status for the closed-row pill.
 * "filled" = the prep completed with content; "skipped" = anything else
 * (failed, running-but-stale, pending). UI-only computation — no backend
 * call. Maps to `pipeline` StatusPill values of `complete`-equivalent hue
 * vs a subdued variant.
 */
function prepStatusPill(
  status: string,
): { label: string; palette: string } {
  if (status === "complete") {
    return {
      label: "Complete",
      palette:
        "bg-status-active-bg text-status-active-fg ring-status-active-ring",
    };
  }
  return {
    label: "Skipped",
    palette:
      "bg-status-opted-out-bg text-status-opted-out-fg ring-status-opted-out-ring",
  };
}

function PrepHistoryEntry({
  row,
}: PrepHistoryEntryProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const stageLabel = row.stage_name ?? "Stage";
  const dateStr = formatDate(row.completed_at ?? row.created_at);
  const pill = prepStatusPill(row.status);

  return (
    <li
      className="overflow-hidden rounded-lg border border-border-default bg-surface-card"
      data-testid={`prep-history-entry-${row.id}`}
    >
      {/* ── Closed row (§5.2): Eyebrow / Stage / Date / StatusPill / Chevron ── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-surface-card-hover"
        aria-expanded={open}
        data-testid={`prep-history-toggle-${row.id}`}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wider text-text-tertiary">
            Prior stage
          </span>
          <span className="text-sm font-medium text-text-primary">
            {stageLabel}
            <span className="ml-2 font-normal text-text-tertiary">
              {dateStr}
            </span>
          </span>
          {/* S1 — provenance line: absent when backend returns no per-prep
              provenance on history rows (graceful degrade). The field does
              not exist on InterviewPrepHistoryRow in v1 — rendered only if
              a future row shape adds it. */}
          {"provenance" in row && typeof row.provenance === "string" ? (
            <span className="mt-0.5 truncate text-xs text-text-secondary">
              {row.provenance}
            </span>
          ) : null}
        </div>
        {/* S1 — right cluster: StatusPill + Chevron */}
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${pill.palette}`}
            data-testid={`prep-history-status-pill-${row.id}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
            {pill.label}
          </span>
          <ChevronDown
            size={16}
            className={`text-text-tertiary transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        </div>
      </button>

      {/* ── Open accordion body: content ── */}
      {open ? (
        <div
          className="border-t border-border-default px-4 py-4"
          data-testid={`prep-history-body-${row.id}`}
        >
          {row.content ? (
            <EvaluationMarkdown source={row.content} />
          ) : (
            <p className="text-sm text-text-tertiary">
              No content available for this prep.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}
